// Commitment Tracker contracts — FE-041.
//
// FE-041 ships the page chrome ahead of the canonical commitment
// REST endpoint. ``api.ts`` calls a placeholder ``/api/v1/commitments/``
// path; until BE-052 lands the page surfaces a safe error state and
// tests mock the hook directly.

export type CommitmentStatus =
  | "backlog"
  | "ready_for_work"
  | "in_progress"
  | "in_review"
  | "done"
  | "cancelled";


export type CommitmentPriority = "high" | "medium" | "low";


export type CommitmentSortKey =
  | "title"
  | "due_date_time"
  | "priority"
  | "status"
  | "updated_at"
  | "commitment_number";


export interface CommitmentTag {
  id: string;
  name: string;
  is_regulatory: boolean;
  /** BE-052 emits these keys alongside ``id`` / ``name`` for the
   * detail surface. They're optional for backwards compatibility
   * with the FE-041 list-row shape that pre-dated BE-052. */
  tag_id?: string;
  slug?: string;
  display_name?: string;
  display_color?: string | null;
}


export interface CommitmentListFilters {
  search?: string;
  status?: CommitmentStatus;
  priority?: CommitmentPriority;
  client_id?: string;
  /** Drill-through filter from the Plan Year detail surface. The
   * Plan Year "View Plan Year Commitments" link sets this so the
   * tracker shows only Commitments tied to that specific plan year
   * (mirrors the Plan Year Work summary's scope). */
  plan_year_id?: string;
  assigned_to?: string;
  category?: string;
  /** Tri-state booleans propagated from dashboard drill-throughs. */
  overdue?: boolean;
  is_blocked?: boolean;
  has_assignee?: boolean;
  has_due_date_time?: boolean;
  on_time?: boolean;
  /** YYYY-MM-DD; surfaced when the dashboard drilled through a CFD
   * snapshot. */
  snapshot_date?: string;
  /** Polish chunk A1: opt the Cancelled lane back into the default
   * board. ``true`` adds the Cancelled column to the standard 5-lane
   * view; ``status`` filters always win over this flag. Default is
   * ``undefined`` (treated as ``false``) so cancelled work stays
   * hidden by default. */
  include_cancelled?: boolean;
  /** FE/BE-043b: archive lifecycle filter. ``true`` flips the tracker
   * into the archived view (only ``archived_at IS NOT NULL`` rows);
   * ``false`` is the active view (the BE default). ``undefined``
   * means active by default. */
  archived?: boolean;
  /** FE/BE-043b: pass-through to the model's ``visibility`` column.
   * Drives the Private Quick Filter (``visibility=private``). */
  visibility?: "team" | "private";
  /** FE-047 (revised): scope the tracker to a single ``Commitment.source``.
   * The Apply/Recent-applications "View Commitments" links use the
   * narrower ``playbook_application_id`` filter; ``source=playbook``
   * is the broader "all Playbook-generated work" filter. */
  source?: "manual" | "playbook";
  /** FE-047 (revised): scope the tracker to a specific Playbook
   * application — the rows the apply spawned, even when the same
   * Account + Plan year was applied multiple times. */
  playbook_application_id?: string;
  /** Touchpoint detail panel asks for the action items that were
   * filed against a specific Touchpoint. Different from the broader
   * ``source=touchpoint`` filter (which would return *every*
   * touchpoint-sourced row across the broker). */
  source_touchpoint_id?: string;
}


export interface CommitmentListQuery {
  filters: CommitmentListFilters;
  sortBy: CommitmentSortKey;
  sortDir: "asc" | "desc";
  limit: number;
  offset: number;
  /** Polish B2: optional advanced-filter tree. Encoded into the
   * ``filter`` query param at fetch time and AND'd with the flat
   * filters at the backend. */
  filterTree?: unknown;
}


export interface CommitmentListRow {
  commitment_id: string;
  commitment_number: string;
  title: string;
  status: CommitmentStatus;
  priority: CommitmentPriority;
  category_slug: string;
  category_name: string | null;
  /** Backend keeps this as ``client_id``; user-facing copy says
   * Account. */
  client_id: string;
  account_name: string | null;
  plan_year_id: string | null;
  assigned_to: string | null;
  assigned_to_display_name: string | null;
  assigned_to_email: string | null;
  /** BE-052 emits these alongside ``assigned_to`` so the
   * detail surface can display every assignment branch (user,
   * job-title placeholder, ClientContact). Optional on the
   * list-row shape because FE-041's table doesn't read them
   * yet. */
  assigned_job_title?: string | null;
  assigned_client_contact_id?: string | null;
  due_date_time: string | null;
  is_blocked: boolean;
  blocked_reason: string | null;
  tags: CommitmentTag[];
  complexity: number | null;
  /** FE/BE-043b: archive lifecycle. ISO timestamp when the row was
   * archived; ``null`` for active Commitments. List rows include
   * the field so list-level UI can render an Archived badge when
   * the Archive Quick Filter is active. */
  archived_at?: string | null;
  archived_by_id?: string | null;
  created_at: string | null;
  updated_at: string | null;
}


/** Per-status totals across the entire filtered set (independent of
 * the visible page slice). Backend always returns the full 6-bucket
 * shape with ``0`` for empty buckets so the FE can index without
 * fallback. */
export type CommitmentStatusCounts = Record<CommitmentStatus, number>;


export interface CommitmentListResponse {
  rows: CommitmentListRow[];
  total: number;
  /** Per-status totals across the entire filtered set. Always
   * returned by the production backend; kept optional on the wire
   * type so test fixtures can stub list responses without
   * reproducing the canonical 6-bucket shape. Component consumers
   * already fall back to ``rows.length`` when the field is absent. */
  status_counts?: CommitmentStatusCounts;
}


// ── Detail (FE-043) ────────────────────────────────────────────────────────


export interface CommitmentDependencyEdge {
  dependency_id: string;
  depends_on_commitment_id: string;
  title: string;
  status: CommitmentStatus;
  dependency_type?: string;
}


export interface CommitmentWatcherSummary {
  user_id: string;
  display_name: string | null;
  email: string | null;
}


export interface CommitmentStatusTransition {
  transition_id: string;
  from_status: CommitmentStatus | null;
  to_status: CommitmentStatus;
  actor_kind: string;
  entered_at: string | null;
}


export interface CommitmentSubtaskSummary {
  commitment_id: string;
  commitment_number: string | number;
  title: string;
  status: CommitmentStatus;
  priority: string | null;
  due_date_time: string | null;
}


export interface CommitmentDetail extends CommitmentListRow {
  /** ``account_id`` is BE-052's outbound alias for ``client_id``. */
  account_id: string | null;
  visibility?: string | null;
  completed_at?: string | null;
  cancelled_at?: string | null;
  cancellation_reason?: string | null;
  started_at?: string | null;
  description: string | null;
  description_plaintext: string | null;
  description_document_id: string | null;
  reviewer_id: string | null;
  reviewer_display_name: string | null;
  reviewer_email: string | null;
  /** ISO timestamp when the assigned reviewer marked this
   * Commitment reviewed via /review. ``null`` until they approve;
   * independent of the done transition (no longer gated). */
  reviewed_at: string | null;
  /** Plan year date range pulled in by the BE-052 detail payload —
   * surfaced as ``YYYY-MM-DD`` strings (effective_start / effective_end)
   * when ``plan_year_id`` is set. */
  plan_year_start: string | null;
  plan_year_end: string | null;
  parent_commitment_id: string | null;
  parent_commitment_title: string | null;
  dependencies: CommitmentDependencyEdge[];
  watchers: CommitmentWatcherSummary[];
  status_transitions: CommitmentStatusTransition[];
  subtasks: CommitmentSubtaskSummary[];
  source: string | null;
  source_playbook_application_id: string | null;
  /** Resolved originating Playbook id — present when the source is
   * ``playbook`` and the underlying ``PlaybookApplication`` row is
   * still readable. The detail page links the source chip to the
   * Playbook editor when this is set. */
  source_playbook_id?: string | null;
  source_playbook_name?: string | null;
  /** ISO timestamp of the originating
   * ``PlaybookApplication.applied_at``. Surfaced under the source
   * chip as "Applied {date}" so the broker has context for *when*
   * a Commitment was spawned from a Playbook. */
  source_applied_at?: string | null;
  source_touchpoint_id: string | null;
  /** User id of the original creator. Used by the FE to gate the
   * Private visibility option to the creator only — the BE enforces
   * the same rule on PATCH. */
  created_by_id?: string | null;
  /** Display name of the original creator, surfaced under the title
   * as "Created on {date} by {name}". Falls back to email, then to
   * a generic placeholder, when the BE didn't resolve a name. */
  created_by_display_name?: string | null;
  created_by_email?: string | null;
  linked_files: unknown[];
}


// ── Commitments-01c — Comments ───────────────────────────────────────────


export interface CommitmentMentionedUser {
  user_id: string;
  display_name: string;
  email: string;
}


/** Discriminator for who authored a Commitment comment.
 *  ``user`` — a human Membership; ``agent`` — BeneAI responding to
 *  an ``@BeneAI`` mention; ``system`` — runtime emitted a
 *  failure/explanation row in-thread so an ``@BeneAI`` never goes
 *  silent. */
export type CommitmentCommentAuthorKind = "user" | "agent" | "system";


export interface CommitmentComment {
  comment_id: string;
  commitment_id: string;
  author_id: string | null;
  author_display_name: string | null;
  author_email: string | null;
  /** Defaults to ``"user"`` on rows that pre-date BeneAI-mentions.
   *  Optional on the wire so test fixtures + future surfaces don't
   *  need to stamp it on every row; consumers fall back to
   *  ``"user"`` when absent. */
  author_kind?: CommitmentCommentAuthorKind;
  author_agent_id?: string | null;
  /** The user whose ``@BeneAI`` prompt triggered an agent / system
   *  reply. Null on plain user comments. */
  triggered_by_id?: string | null;
  /** Comment the row is replying to. Null for top-level rows. */
  in_reply_to_id?: string | null;
  body: string;
  mentioned_user_ids: string[];
  mentioned_users: CommitmentMentionedUser[];
  created_at: string | null;
  updated_at: string | null;
}


export interface CommitmentCommentsListResponse {
  rows: CommitmentComment[];
  total: number;
}


export interface AddCommitmentCommentInput {
  body: string;
  mention_user_ids?: string[];
}


/** Response from POST /commitments/{id}/comments.
 *
 *  The base shape is the freshly-persisted human comment. When the
 *  body contained ``@BeneAI``, the backend drove a synchronous
 *  BeneAI turn and persisted the reply as a sibling comment;
 *  ``agent_reply`` carries that row so the FE can splice it into
 *  the list cache without a follow-up GET. ``agent_reply`` is
 *  absent on plain comments. */
export type AddCommitmentCommentResponse = CommitmentComment & {
  agent_reply?: CommitmentComment;
};


// ── FE-048 — Commitment attachments ──────────────────────────────────────


export interface CommitmentAttachmentRow {
  link_id: string;
  file_id: string;
  filename: string;
  content_type: string;
  size_bytes: number;
  linked_at: string | null;
  linked_by_id: string | null;
  linked_by_email: string | null;
  document_node_id: string | null;
  document_node_path: string | null;
}


export interface CommitmentAttachmentsListResponse {
  rows: CommitmentAttachmentRow[];
  total: number;
}


export interface CommitmentAttachmentPreviewResponse {
  url: string;
  filename: string;
  content_type: string;
  size_bytes: number;
}


export interface CommitmentAttachmentLinkResponse {
  link_id: string;
  file_id: string;
  document_node_id: string | null;
  document_node_path: string | null;
  is_dedup?: boolean;
  filename?: string;
  content_type?: string;
  size_bytes?: number;
}


// ── Form / mutations (FE-043) ─────────────────────────────────────────────


export type CommitmentFormMode = "create" | "edit";


/** Controlled-input form state. Strings stay strings so the inputs
 * don't have to special-case null/undefined; the API helper converts
 * back to nullable primitives at submit time. */
export interface CommitmentFormValues {
  title: string;
  category_slug: string;
  /** UI label says "Account" — frontend keeps the v2 name. The
   * serializer maps this onto the BE-012 ``client_id`` column at
   * submit time. */
  account_id: string;
  plan_year_id: string;
  description: string;
  /** BlockNote document the rich editor created on first edit. Empty
   * string means "no document yet" — the description is plaintext-only
   * (or empty). The submit serializer maps this onto the BE-052
   * ``description_document_id`` column. */
  description_document_id: string;
  priority: "" | CommitmentPriority;
  /** Stored as a string so the controlled input round-trips cleanly;
   * validation rejects anything outside {1, 2, 3, 5}. */
  complexity: string;
  due_date_time: string;
  assigned_to: string;
  assigned_job_title: string;
  assigned_client_contact_id: string;
  reviewer_id: string;
  /** Comma-separated list of tag slugs. Future chunks may swap to a
   * chip picker; the parser already accepts that shape. */
  tag_slugs: string;
  /** Visibility dropdown selection. Maps directly to BE-052's
   * ``visibility`` column. ``"team"`` (default) is org-wide;
   * ``"private"`` restricts the row to the assignee. The
   * employer-portal value isn't on the wire yet — the dropdown
   * surfaces it as disabled "Coming soon". */
  visibility: CommitmentFormVisibility;
  /** Hidden field — set by the modal caller when the action item is
   * being created from a Touchpoint detail panel. Carried through
   * the form into the create payload so the BE stamps
   * ``source=touchpoint`` and the back-link FK. */
  source_touchpoint_id: string;
}


/** Visibility values the form can hold. ``employer`` is reserved for
 * the future employer portal — not accepted by the BE today, so the
 * dropdown disables it and the form never emits it. */
export type CommitmentFormVisibility = "team" | "private";


export interface CreateCommitmentInput {
  title: string;
  category_slug: string;
  /** Backend column name. Either ``client_id`` or ``account_id`` may
   * be sent — the api helper normalizes ``account_id`` →
   * ``client_id`` so the on-the-wire payload always uses the
   * BE-012 column name. */
  client_id?: string | null;
  account_id?: string | null;
  plan_year_id?: string | null;
  parent_commitment_id?: string | null;
  description?: string | null;
  description_document_id?: string | null;
  priority?: CommitmentPriority;
  complexity?: number;
  due_date_time?: string | null;
  assigned_to?: string | null;
  assigned_job_title?: string | null;
  assigned_client_contact_id?: string | null;
  reviewer_id?: string | null;
  is_blocked?: boolean;
  blocked_reason?: string | null;
  visibility?: "team" | "private";
  tag_ids?: string[];
  /** Frontend convenience — serializer translates to ``tag_ids`` when
   * the SPA has the ``slug → id`` map; until then the backend's
   * ``tag_slugs`` shorthand keeps the contract honest. */
  tag_slugs?: string[];
  /** Stamps ``source=touchpoint`` + the back-link FK so the new row
   * surfaces on the Touchpoint detail panel. */
  source_touchpoint_id?: string | null;
}


export type UpdateCommitmentInput = Partial<
  Omit<CreateCommitmentInput, "parent_commitment_id" | "is_blocked">
>;


export interface CommitmentMutationResponse {
  commitment_id: string;
  /** Detail endpoints emit the full ``CommitmentDetail`` shape; the
   * mutation helpers surface only the id so callers can navigate
   * without depending on every detail key being present in the
   * response. */
  [key: string]: unknown;
}


export interface CommitmentTransitionInput {
  to_status: CommitmentStatus;
  cancellation_reason?: string | null;
  source_ref?: string | null;
}


export class CommitmentNotFoundError extends Error {
  constructor(public readonly commitmentId: string) {
    super(`Commitment ${commitmentId} not found`);
    this.name = "CommitmentNotFoundError";
  }
}
